'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';

// フォーム処理状態の型定義
export type FormProcessingState = {
  isProcessing: boolean;
  hasStartedProcessing: boolean;
  processingStage: 'idle' | 'submitting' | 'uploading' | 'processing' | 'completed' | 'error';
};

interface FormProcessingContextType {
  formState: FormProcessingState;
  setFormState: (state: Partial<FormProcessingState>) => void;
  startProcessing: () => void;
  completeProcessing: () => void;
  resetProcessing: () => void;
}

const FormProcessingContext = createContext<FormProcessingContextType | undefined>(undefined);

export const useFormProcessing = () => {
  const context = useContext(FormProcessingContext);
  if (context === undefined) {
    throw new Error('useFormProcessing must be used within a FormProcessingProvider');
  }
  return context;
};

interface FormProcessingProviderProps {
  children: ReactNode;
}

const FormProcessingProvider: React.FC<FormProcessingProviderProps> = ({ children }) => {
  const [formState, setFormStateInternal] = useState<FormProcessingState>({
    isProcessing: false,
    hasStartedProcessing: false,
    processingStage: 'idle'
  });

  const setFormState = (newState: Partial<FormProcessingState>) => {
    setFormStateInternal(prev => ({ ...prev, ...newState }));
  };

  const startProcessing = () => {
    setFormState({
      isProcessing: true,
      hasStartedProcessing: true,
      processingStage: 'submitting'
    });
  };

  const completeProcessing = () => {
    setFormState({
      isProcessing: false,
      processingStage: 'completed'
    });
  };

  const resetProcessing = () => {
    setFormState({
      isProcessing: false,
      hasStartedProcessing: false,
      processingStage: 'idle'
    });
  };

  const value: FormProcessingContextType = {
    formState,
    setFormState,
    startProcessing,
    completeProcessing,
    resetProcessing
  };

  return (
    <FormProcessingContext.Provider value={value}>
      {children}
    </FormProcessingContext.Provider>
  );
};

export { FormProcessingProvider }; 